import { useState, useRef, useCallback, useEffect } from 'react';

const useCountDown = (initialTime = 60000, interval = 1000) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);
  const targetTimeRef = useRef(0);
  const lastUpdateRef = useRef(0);
  const isActiveRef = useRef(false);
  
  // Enhanced cleanup function
  const cleanup = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    isActiveRef.current = false;
  }, []);

  // Start countdown
  const start = useCallback((duration) => {
    cleanup(); // Clear any existing animation frame
    
    const startDuration = typeof duration === 'number' ? duration : initialTime;
    if (startDuration <= 0) {
      setTimeLeft(0);
      return;
    }
    
    const now = Date.now();
    startTimeRef.current = now;
    targetTimeRef.current = now + startDuration;
    lastUpdateRef.current = now;
    isActiveRef.current = true;
    
    setTimeLeft(startDuration);
    
    // Use requestAnimationFrame for smoother updates with enhanced error handling
    const tick = () => {
      // Check if component is still active
      if (!isActiveRef.current) {
        return;
      }
      
      try {
        const now = Date.now();
        const remaining = Math.max(0, targetTimeRef.current - now);
        
        // Only update if enough time has passed (based on interval)
        if (now - lastUpdateRef.current >= interval) {
          setTimeLeft(remaining);
          lastUpdateRef.current = now;
        }
        
        if (remaining > 0 && isActiveRef.current) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          // Cleanup when countdown finishes
          cleanup();
        }
      } catch (error) {
        console.warn('Error in countdown tick:', error);
        cleanup();
      }
    };
    
    rafRef.current = requestAnimationFrame(tick);
  }, [initialTime, interval, cleanup]);

  // Pause countdown
  const pause = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // Resume countdown  
  const resume = useCallback(() => {
    if (timeLeft > 0) {
      start(timeLeft);
    }
  }, [timeLeft, start]);

  // Reset countdown
  const reset = useCallback(() => {
    cleanup();
    setTimeLeft(0);
  }, [cleanup]);

  // Enhanced cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return [timeLeft, { start, pause, resume, reset }];
};

export default useCountDown;